import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Linkedin, MapPin, Phone, Mail, Send } from "lucide-react";

import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { COMPANY, EXHIBITION_IMAGES } from "@/data/content";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.newsletter.errorRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.pages.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || t.newsletter.errorGeneric;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <PageHero
        eyebrow={t.pages.contact.eyebrow}
        title={t.pages.contact.title}
        subtitle={t.pages.contact.subtitle}
        image={EXHIBITION_IMAGES[2]}
      />

      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: form */}
          <Reveal className="lg:col-span-7">
            <p className="eyebrow">{t.pages.contact.formEyebrow}</p>
            <h2 className="heading-display mt-3 text-4xl sm:text-5xl uppercase">
              {t.pages.contact.formTitle}
            </h2>
            <form onSubmit={submit} className="mt-12 space-y-2" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={handle("name")}
                  placeholder={t.pages.contact.name}
                  className="w-full bg-transparent border-b border-black/20 py-4 text-base placeholder:text-black/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none"
                />
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={handle("email")}
                  placeholder={t.pages.contact.email}
                  className="w-full bg-transparent border-b border-black/20 py-4 text-base placeholder:text-black/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none"
                />
              </div>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={handle("message")}
                placeholder={t.pages.contact.message}
                rows={5}
                className="w-full bg-transparent border-b border-black/20 py-4 text-base placeholder:text-black/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none resize-none"
              />
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="mt-8 inline-flex items-center gap-3 bg-[#C20E1A] text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] font-bold hover:bg-black transition-colors duration-500 disabled:opacity-60"
              >
                {loading ? t.pages.contact.submitting : t.pages.contact.cta} <Send size={14} />
              </button>
            </form>
          </Reveal>

          {/* Right: info */}
          <Reveal className="lg:col-span-5 space-y-8" delay={120}>
            <div>
              <p className="eyebrow">{t.pages.contact.infoEyebrow}</p>
              <h3 className="heading-display mt-3 text-3xl uppercase">{t.pages.contact.infoTitle}</h3>
            </div>
            <div className="space-y-4 text-base text-black/80">
              <p className="flex gap-3"><MapPin className="text-[#C20E1A] mt-1" size={18} /> {COMPANY.address}</p>
              <p className="flex gap-3"><Phone className="text-[#C20E1A] mt-1" size={18} /> {COMPANY.phone}</p>
              <p className="flex gap-3"><Mail className="text-[#C20E1A] mt-1" size={18} /> <a className="link-red" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></p>
            </div>
            <div className="aspect-[4/3] w-full overflow-hidden border border-black/10">
              <iframe
                title="Artful Contemporary location"
                src="https://www.google.com/maps?q=Anton-Kux-Str.+2,+41460+Neuss,+Germany&output=embed"
                className="w-full h-full grayscale-[40%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="flex items-center gap-3">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="contact-instagram"
                className="h-11 w-11 inline-flex items-center justify-center border border-black/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] hover:text-white transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="contact-linkedin"
                className="h-11 w-11 inline-flex items-center justify-center border border-black/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] hover:text-white transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
